import gql from "graphql-tag";

export enum PnlBreakdownFiltersEnum {
  /** return one breakdown per sku */
  sku = "sku",
  /** return one breakdown per operator */
  operator = "operator",
  /** return one breakdown per shiftName */
  shiftName = "shift_name",
}

type Breakdown = {
  durationMs: number;
  numberOfEvents: number;
  netOutput: number;
  targetOutput: number;
  outputLoss: number;
  averageDurationMs: number;
};

export type TagBreakdown = Breakdown & {
  tagName: string;
};

export type BreakdownWithTagsType = Breakdown & {
  tags: TagBreakdown[];
};

type Producing = BreakdownWithTagsType & {
  effectiveProducing: Breakdown;
  qualityGainLossTime: number;
  slowdownGainLossTime: number;
};

type Unknown = Breakdown & {
  minorStops: {
    durationMs: number;
    numberOfEvents: number;
    netOutput: number | null;
    targetOutput: number | null;
    outputLoss: number;
    averageDurationMs: number;
  };
  numberOfStops: number | null;
};

export type PnlBreakdown = {
  ge: number;
  lt: number;
  shiftName: string | null;
  sku: string | null;
  skuDescription: string | null;
  skuTargetRate?: number;
  skuRateDenomMs?: number;
  operator: string | null;
  machemetryIds: string[];
  processNames: string[];

  collectedTime: number;
  operationTime: number;
  opportunityTime: number;
  productionTargetOutput: number;
  outputLossSum: number;
  productionTime: number;
  producing: Producing;
  planned: BreakdownWithTagsType;
  waiting: BreakdownWithTagsType;
  unplanned: BreakdownWithTagsType;
  unknown: Unknown;
  notScheduled: BreakdownWithTagsType;
  uncollected: Breakdown;

  oeePercent: number | null;
  oeePercentTopDown: number | null;
  oeePercentBottomUp: number | null;

  teepPercent: number | null;
  teepPercentBottomUp: number | null;

  availabilityPercent: number | null;
  uptimePercent: number | null;

  performancePercent: number | null;
  performancePercentBottomUp: number | null;

  producingEfficiencyPercent: number | null;
  producingEfficiencyPercentBottomUp: number | null;

  qualityYieldPercent: number | null;
  uslePercent: number | null;
  operationalAvailabilityPercent: number | null;
  efficiencyPercent: number | null;
  pureEquipmentUptimePercent: number | null;
  opportunityPercent: number | null;
  taggingPercent: number | null;
  taggingByEventPercent: number | null;
  taggedNumberOfEvents: number | null;
  untaggedNumberOfEvents: number | null;

  netOutput: number;
  grossOutput: number;
  rejectOutput: number;
  manualRejectOutput: number;
  totalPotentialOutput: number;

  netTicks: number;
  grossTicks: number;
  rejectTicks: number;

  ticksPort1: number;
  ticksPort2: number;
  ticksPort3: number;
  ticksPort4: number;

  meanTimeBetweenFailures: number | null;
  meanTimeToRecovery: number | null;
  avgCycleTime: number | null;

  customMetric01Value: number | null;
  customMetric02Value: number | null;
  customMetric03Value: number | null;
  customMetric04Value: number | null;
  customMetric05Value: number | null;
  customMetric06Value: number | null;
  customMetric07Value: number | null;
  customMetric08Value: number | null;
  untaggedPercent: number | null;
  notScheduledPercent: number | null;
};

export type PnlBreakdownWithMultiDataKey = PnlBreakdown & {
  multiDataKey: string;
};

export type PnlBreakdownFilteredWithMultiDataKey = {
  pnlBreakdowns: PnlBreakdown[];
  multiDataKey: string;
};
export type ProcessGroupAndMachemetryIdsInput = {
  processGroupName: string;
  machemetryIds?: string[];
};
const PNL_BREAKDOWN_FRAGMENT = gql`
  fragment PnlBreakdownFields on PnlBreakdown {
    ge
    lt
    shiftName
    sku
    skuDescription
    skuTargetRate
    skuRateDenomMs
    operator
    machemetryIds
    processNames
    collectedTime
    operationTime
    opportunityTime
    productionTargetOutput
    productionTime
    outputLossSum
    producing {
      durationMs
      numberOfEvents
      outputLoss
      averageDurationMs
      tags {
        tagName
        durationMs
        numberOfEvents
        outputLoss
        averageDurationMs
        netOutput
        targetOutput
      }
      effectiveProducing {
        durationMs
        numberOfEvents
        outputLoss
        averageDurationMs
        netOutput
        targetOutput
      }
      qualityGainLossTime
      slowdownGainLossTime
      netOutput
      targetOutput
    }
    planned {
      durationMs
      numberOfEvents
      outputLoss
      averageDurationMs
      netOutput
      targetOutput
      tags {
        tagName
        durationMs
        numberOfEvents
        outputLoss
        averageDurationMs
        netOutput
        targetOutput
      }
    }
    waiting {
      durationMs
      numberOfEvents
      outputLoss
      averageDurationMs
      netOutput
      targetOutput
      tags {
        tagName
        durationMs
        numberOfEvents
        outputLoss
        averageDurationMs
        netOutput
        targetOutput
      }
    }
    unplanned {
      durationMs
      numberOfEvents
      outputLoss
      averageDurationMs
      netOutput
      targetOutput
      tags {
        tagName
        durationMs
        numberOfEvents
        outputLoss
        averageDurationMs
        netOutput
        targetOutput
      }
    }
    unknown {
      durationMs
      numberOfEvents
      outputLoss
      averageDurationMs
      netOutput
      targetOutput
      minorStops {
        durationMs
        numberOfEvents
        outputLoss
        averageDurationMs
        netOutput
        targetOutput
      }
      numberOfStops
    }
    notScheduled {
      durationMs
      numberOfEvents
      outputLoss
      netOutput
      targetOutput
      averageDurationMs
      tags {
        tagName
        durationMs
        numberOfEvents
        outputLoss
        averageDurationMs
        netOutput
        targetOutput
      }
    }
    uncollected {
      durationMs
      numberOfEvents
      outputLoss
      netOutput
      targetOutput
      averageDurationMs
    }
    oeePercent
    oeePercentTopDown
    oeePercentBottomUp

    teepPercent
    teepPercentBottomUp

    availabilityPercent
    uptimePercent

    performancePercent
    performancePercentBottomUp

    producingEfficiencyPercent
    producingEfficiencyPercentBottomUp

    qualityYieldPercent
    netOutput
    grossOutput
    rejectOutput
    manualRejectOutput
    uslePercent
    operationalAvailabilityPercent
    efficiencyPercent
    pureEquipmentUptimePercent
    opportunityPercent
    taggingPercent
    totalPotentialOutput
    taggingByEventPercent
    taggedNumberOfEvents
    untaggedNumberOfEvents

    netTicks
    grossTicks
    rejectTicks

    ticksPort1
    ticksPort2
    ticksPort3
    ticksPort4

    meanTimeToRecovery
    meanTimeBetweenFailures
    avgCycleTime

    customMetric01Value
    customMetric02Value
    customMetric03Value
    customMetric04Value
    customMetric05Value
    customMetric06Value
    customMetric07Value
    customMetric08Value
    untaggedPercent
    notScheduledPercent
  }
`;

/**
 * @param filter: {@link PnlBreakdownFiltersEnum} -> like "group by", returns one PnlBreakdown per ["sku" | "operator" | "shift_name"]
 * @param shouldFilterByMachemetryId: like "group by", true -> returns one PnlBreakdown per machine; false -> returns one overall big breakdown (as the first list item)
 */
export const MIDGARD_PNL_BREAKDOWN_QUERY = gql`
  query PnlBreakdown(
    $ge: BigInt!
    $lt: BigInt!
    $machemetryIds: [String]
    $filter: String
    $shiftsToExclude: [String]
    $shouldFilterByMachemetryId: Boolean
  ) {
    pnlBreakdown(
      ge: $ge
      lt: $lt
      machemetryIds: $machemetryIds
      filter: $filter
      shiftsToExclude: $shiftsToExclude
      shouldFilterByMachemetryId: $shouldFilterByMachemetryId
    ) {
      ...PnlBreakdownFields
    }
  }
  ${PNL_BREAKDOWN_FRAGMENT}
`;
/**
 * @param filter: {@link PnlBreakdownFiltersEnum} -> like "group by", returns one PnlBreakdown per ["sku" | "operator" | "shift_name"]
 * @param shouldFilterByMachemetryId: like "group by", true -> returns one PnlBreakdown per machine; false -> returns one overall big breakdown (as the first list item)
 */
export const ASKR_PNL_BREAKDOWN_QUERY = gql`
  query PnlBreakdown(
    $ge: String!
    $lt: String!
    $machemetryIds: [String]
    $filter: String
    $shouldFilterByMachemetryId: Boolean
  ) {
    pnlBreakdown(
      range: { ge: $ge, lt: $lt }
      machemetryIds: $machemetryIds
      filter: $filter
      shouldFilterByMachemetryId: $shouldFilterByMachemetryId
    ) {
      ...PnlBreakdownFields
    }
  }
  ${PNL_BREAKDOWN_FRAGMENT}
`;
